import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Send, Settings, Bell, BellOff, Phone, TestTube, Check, Loader2, Calendar, Clock, Mail, Zap, Copy, Globe, ShieldCheck, RefreshCw, Webhook, Plus, Trash2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import WhatsAppMessageLogCard from '../components/WhatsAppMessageLogCard';
import WhatsAppInboundLogCard from '../components/WhatsAppInboundLogCard';
import WhatsAppDisputesCard from '../components/WhatsAppDisputesCard';
import WhatsAppConversationPanel from '../components/WhatsAppConversationPanel';

export default function WhatsAppSettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [newApiKey, setNewApiKey] = useState('');
  const [sendingDigest, setSendingDigest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);
  const [testSmsPhone, setTestSmsPhone] = useState('');
  const [testingSms, setTestingSms] = useState(false);
  // iter199 — Meta Cloud API state
  const [newMetaAccessToken, setNewMetaAccessToken] = useState('');
  const [newMetaAppSecret, setNewMetaAppSecret] = useState('');
  // iter213 — Send test via APPROVED TEMPLATE (works outside 24h window)
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [testTplName, setTestTplName] = useState('');
  const [testTplPhone, setTestTplPhone] = useState('');
  const [testingTpl, setTestingTpl] = useState(false);
  // iter213.1 — Batch test (fire one of every approved template)
  const [testingBatch, setTestingBatch] = useState(false);
  const [batchResults, setBatchResults] = useState(null); // { to, total, sent, failed, results: [...] }
  // iter199.1 — Template auto-sync state
  const [templates, setTemplates] = useState({ templates: [], catalog: [] });
  const [syncingTpl, setSyncingTpl] = useState(false);
  const [refreshingTpl, setRefreshingTpl] = useState(false);
  const [tplHealth, setTplHealth] = useState(null);
  const [deletingTpl, setDeletingTpl] = useState(null); // name being deleted
  // iter213.3 — Per-template selection for selective sync
  const [selectedNotifTypes, setSelectedNotifTypes] = useState(new Set());
  // iter214.1 — Show Meta rejection reasons after a sync so admin can act
  const [syncResult, setSyncResult] = useState(null); // { submitted, skipped, failed, checked_at }
  // iter208 — Webhook fan-out state
  const [fanout, setFanout] = useState({ downstreams: [], splitease_webhook_url: '', splitease_verify_token: '', app_secret_configured: false });
  const [fanoutLog, setFanoutLog] = useState([]);
  const [newFanoutLabel, setNewFanoutLabel] = useState('');
  const [newFanoutUrl, setNewFanoutUrl] = useState('');
  const [savingFanout, setSavingFanout] = useState(false);
  const [refreshingFanoutLog, setRefreshingFanoutLog] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/whatsapp/settings');
      setSettings(res.data);
    } catch (error) {
      toast.error('Failed to load WhatsApp settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/whatsapp/settings', {
        api_key: newApiKey || null,
        enabled: settings.enabled,
        country_code: settings.country_code,
        notify_on_fine: settings.notify_on_fine,
        notify_on_expense_approval: settings.notify_on_expense_approval,
        notify_on_expense_submission: settings.notify_on_expense_submission,
        notify_on_session: settings.notify_on_session,
        weekly_digest: settings.weekly_digest,
        digest_day: settings.digest_day,
        digest_hour_ist: settings.digest_hour_ist,
        approval_reminders: settings.approval_reminders !== false,
        signup_ping: settings.signup_ping !== false,
        invite_reminders: settings.invite_reminders !== false,
        dnd_start_hour: settings.dnd_start_hour,
        dnd_end_hour: settings.dnd_end_hour,
        sms_fallback_enabled: settings.sms_fallback_enabled,
        sms_sender_profile_id: settings.sms_sender_profile_id || null,
        email_enabled: settings.email_enabled,
        email_sender_profile_id: settings.email_sender_profile_id || null,
        email_from_address: settings.email_from_address || null,
        whatsapp_from_number: settings.whatsapp_from_number || null,
        // iter199 — Meta Cloud API
        provider: settings.provider || 'zavu',
        meta_app_id: settings.meta_app_id || null,
        meta_phone_number_id: settings.meta_phone_number_id || null,
        meta_business_account_id: settings.meta_business_account_id || null,
        meta_graph_version: settings.meta_graph_version || 'v22.0',
        meta_outside_window_behavior: settings.meta_outside_window_behavior || 'skip',
        meta_access_token: newMetaAccessToken || null,
        meta_app_secret: newMetaAppSecret || null,
      });
      toast.success('Settings saved!');
      setNewApiKey('');
      setNewMetaAccessToken('');
      setNewMetaAppSecret('');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // iter199 — copy helper for verify-token / webhook URL
  const copyToClipboard = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(`${label} copied`);
    } catch {
      toast.error('Could not copy');
    }
  };

  // iter213 — Approved templates picker + test send
  const fetchApprovedTemplates = async () => {
    try {
      const res = await api.get('/whatsapp/approved-templates');
      const list = res.data?.templates || [];
      setApprovedTemplates(list);
      // Preserve current pick if still APPROVED; otherwise default to first
      if (!list.find((t) => t.name === testTplName)) {
        setTestTplName(list[0]?.name || '');
      }
    } catch {
      /* silent — UI shows empty-state hint */
    }
  };

  // iter262 — template health: which notif types have NO approved template
  const fetchTemplateHealth = async () => {
    try {
      const res = await api.get('/whatsapp/templates/health');
      setTplHealth(res.data);
    } catch {
      /* silent */
    }
  };

  const handleTestTemplate = async () => {
    if (!testTplPhone.trim()) {
      toast.error('Enter a phone number to test');
      return;
    }
    if (!testTplName) {
      toast.error('Pick an approved template first');
      return;
    }
    setTestingTpl(true);
    try {
      const res = await api.post('/whatsapp/test-template', {
        phone: testTplPhone,
        template_name: testTplName,
      });
      toast.success(`Template '${res.data.template_name}' sent — wamid ${res.data.message_id || 'OK'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Template test failed');
    } finally {
      setTestingTpl(false);
    }
  };

  // iter213.1 — Fire ONE of EVERY approved template (smoke-test the whole notif pipeline)
  const handleTestTemplateBatch = async () => {
    if (!testTplPhone.trim()) {
      toast.error('Enter a phone number first');
      return;
    }
    if (approvedTemplates.length === 0) {
      toast.error('No approved templates to test');
      return;
    }
    const count = approvedTemplates.length;
    if (!window.confirm(`Send ${count} test WhatsApp messages (one per approved template) to ${testTplPhone}? This may take ${Math.ceil(count * 0.5)}s.`)) {
      return;
    }
    setTestingBatch(true);
    setBatchResults(null);
    const toastId = toast.loading(`Sending ${count} test messages…`);
    try {
      const res = await api.post('/whatsapp/test-template-batch', { phone: testTplPhone });
      const data = res.data || {};
      setBatchResults(data);
      if (data.failed === 0) {
        toast.success(`All ${data.sent}/${data.total} templates delivered to ${data.to}`, { id: toastId });
      } else {
        toast.error(`${data.sent}/${data.total} sent, ${data.failed} failed — see details below`, { id: toastId });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Batch test failed', { id: toastId });
    } finally {
      setTestingBatch(false);
    }
  };

  // iter199.1 — Template auto-sync handlers
  const fetchTemplates = async () => {
    try {
      const res = await api.get('/whatsapp/templates');
      setTemplates(res.data);
    } catch {
      /* silent — UI shows "not configured" hint */
    }
  };

  useEffect(() => {
    if (settings?.provider === 'meta') fetchTemplates();
    if (settings?.provider === 'meta') { fetchApprovedTemplates(); fetchTemplateHealth(); }
  }, [settings?.provider]);

  const handleSyncTemplates = async (notifTypesFilter = null) => {
    setSyncingTpl(true);
    setSyncResult(null); // iter214.1 — clear stale result
    let toastId;
    try {
      // iter211.1 — sync is now a background job; we poll the status endpoint
      // so the request doesn't hit Cloudflare's 100s proxy timeout.
      // iter213.3 — optional notifTypesFilter for selective sync.
      const reqBody = notifTypesFilter && notifTypesFilter.length > 0
        ? { notif_types: notifTypesFilter }
        : {};
      const startRes = await api.post('/whatsapp/templates/sync', reqBody);
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error('No job id returned');
      const scopeLabel = notifTypesFilter && notifTypesFilter.length > 0
        ? `${notifTypesFilter.length} selected template(s)`
        : 'templates';
      toastId = toast.loading(`Syncing ${scopeLabel} in background — this can take 1-3 min…`);

      const deadline = Date.now() + 5 * 60 * 1000; // 5 min hard cap
      let finished = false;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const st = await api.get(`/whatsapp/templates/sync/status/${jobId}`);
          const job = st.data || {};
          if (job.status === 'success') {
            const r = job.result || {};
            const submitted = r.submitted || [];
            const skipped = r.skipped || [];
            const failed = r.failed || [];
            setSyncResult(r);
            if (failed.length > 0) {
              // iter214.1 — surface the ACTUAL Meta rejection reason so admin
              // can fix the template payload instead of guessing.
              const firstErr = failed[0] || {};
              const errMsg = firstErr.error || firstErr.code || 'unknown';
              const errName = firstErr.notif_type || firstErr.name || 'template';
              toast.error(
                `Sync: ${submitted.length} submitted, ${failed.length} failed. ${errName} → ${errMsg}`,
                { id: toastId, duration: 15000 },
              );
            } else {
              toast.success(
                `Sync done — submitted ${submitted.length}, skipped ${skipped.length}`,
                { id: toastId },
              );
            }
            finished = true;
            break;
          }
          if (job.status === 'error') {
            toast.error(`Template sync failed: ${job.error || 'unknown error'}`, { id: toastId });
            finished = true;
            break;
          }
          // pending / running — keep polling
        } catch (pollErr) {
          // Transient network blip — keep polling until deadline
          if (pollErr?.response?.status === 404) {
            toast.error('Sync job lost — please retry', { id: toastId });
            finished = true;
            break;
          }
        }
      }
      if (!finished) {
        toast.error('Sync still running after 5 min — check templates later.', { id: toastId });
      }
      fetchTemplates();
      fetchApprovedTemplates();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Template sync failed';
      if (toastId) toast.error(msg, { id: toastId });
      else toast.error(msg);
    } finally {
      setSyncingTpl(false);
    }
  };

  const handleRefreshTemplates = async () => {
    setRefreshingTpl(true);
    try {
      await api.post('/whatsapp/templates/refresh');
      toast.success('Refreshed from Meta');
      fetchTemplates();
      fetchApprovedTemplates();
      fetchTemplateHealth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshingTpl(false);
    }
  };

  const handleDeleteTemplate = async (name) => {
    if (!window.confirm(`Delete template "${name}" from Meta? This is permanent and the name will be locked for 30 days.`)) return;
    setDeletingTpl(name);
    try {
      await api.delete(`/whatsapp/templates/${encodeURIComponent(name)}`);
      toast.success(`Deleted ${name}`);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingTpl(null);
    }
  };

  // iter208 — Webhook fan-out handlers
  const fetchFanout = async () => {
    try {
      const res = await api.get('/whatsapp/fanout/config');
      setFanout(res.data);
    } catch {
      /* silent */
    }
  };

  const fetchFanoutLog = async () => {
    setRefreshingFanoutLog(true);
    try {
      const res = await api.get('/whatsapp/fanout/log?limit=20');
      setFanoutLog(res.data.entries || []);
    } catch {
      /* silent */
    } finally {
      setRefreshingFanoutLog(false);
    }
  };

  useEffect(() => {
    if (settings?.provider === 'meta') {
      fetchFanout();
      fetchFanoutLog();
    }
  }, [settings?.provider]);

  const handleAddDownstream = async () => {
    const url = newFanoutUrl.trim();
    const label = newFanoutLabel.trim();
    if (!url) {
      toast.error('Enter a downstream URL');
      return;
    }
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      toast.error('URL must start with http:// or https://');
      return;
    }
    const nextList = [
      ...(fanout.downstreams || []),
      { label: label || url, url, enabled: true },
    ];
    setSavingFanout(true);
    try {
      const res = await api.put('/whatsapp/fanout/config', { downstreams: nextList });
      setFanout({ ...fanout, downstreams: res.data.downstreams || [] });
      setNewFanoutLabel('');
      setNewFanoutUrl('');
      toast.success('Downstream added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add downstream');
    } finally {
      setSavingFanout(false);
    }
  };

  const handleToggleDownstream = async (idx, enabled) => {
    const nextList = (fanout.downstreams || []).map((d, i) => (i === idx ? { ...d, enabled } : d));
    setSavingFanout(true);
    try {
      const res = await api.put('/whatsapp/fanout/config', { downstreams: nextList });
      setFanout({ ...fanout, downstreams: res.data.downstreams || [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update downstream');
    } finally {
      setSavingFanout(false);
    }
  };

  const handleDeleteDownstream = async (idx) => {
    const target = (fanout.downstreams || [])[idx];
    if (!target) return;
    if (!window.confirm(`Remove "${target.label}"?\n${target.url}`)) return;
    const nextList = (fanout.downstreams || []).filter((_, i) => i !== idx);
    setSavingFanout(true);
    try {
      const res = await api.put('/whatsapp/fanout/config', { downstreams: nextList });
      setFanout({ ...fanout, downstreams: res.data.downstreams || [] });
      toast.success('Downstream removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove downstream');
    } finally {
      setSavingFanout(false);
    }
  };

  const handleCopyWebhookUrl = () => {
    if (!fanout.splitease_webhook_url) return;
    navigator.clipboard.writeText(fanout.splitease_webhook_url);
    toast.success('Webhook URL copied');
  };

  const handleCopyVerifyToken = () => {
    if (!fanout.splitease_verify_token) return;
    navigator.clipboard.writeText(fanout.splitease_verify_token);
    toast.success('Verify token copied');
  };

  const handleTest = async () => {
    if (!testPhone.trim()) {
      toast.error('Enter a phone number to test');
      return;
    }
    setTesting(true);
    try {
      const res = await api.post('/whatsapp/test', { phone: testPhone });
      toast.success(`Test message sent! ID: ${res.data.message_id || 'OK'}`);
    } catch (error) {
      // iter213 — backend now raises 400 with actionable hint for 131047 etc.
      toast.error(error.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await api.post('/whatsapp/send-digest');
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail.trim() || !testEmail.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setTestingEmail(true);
    try {
      const res = await api.post('/whatsapp/test-email', { email: testEmail });
      if (res.data.error) toast.error(`Failed: ${res.data.error}`);
      else toast.success(`Test email sent! ID: ${res.data.message_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test email failed');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestSms = async () => {
    if (!testSmsPhone.trim()) {
      toast.error('Enter a phone number');
      return;
    }
    setTestingSms(true);
    try {
      const res = await api.post('/whatsapp/test-sms', { phone: testSmsPhone });
      toast.success(`Test SMS sent! ID: ${res.data.message_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test SMS failed');
    } finally {
      setTestingSms(false);
    }
  };

  if (!user?.is_super_admin) {
    return (
      <div className="p-6 text-center" data-testid="whatsapp-forbidden">
        <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="font-display text-xl font-bold">Admin Only</h2>
        <p className="text-muted-foreground mt-2">Only super admins can configure WhatsApp.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="p-6 text-center text-muted-foreground">Loading settings...</div>;
  }

  return (
    <div className="p-6 max-w-2xl lg:max-w-none mx-auto space-y-6" data-testid="whatsapp-settings-page">
      <div>
        <h1 className="font-display text-2xl font-bold flex items-center gap-2">
          <MessageCircle className="w-6 h-6 text-green-500" />
          WhatsApp Settings
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Configure WhatsApp notifications via Zavu</p>
      </div>

      {/* API Key Section */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="p-5 border-2 rounded-2xl space-y-4" data-testid="api-key-card">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-lg">API Configuration</h3>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold uppercase tracking-wide">Zavu API Key</Label>
            {settings?.api_key_set && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <Check className="w-3 h-3" /> Key configured: {settings.api_key_masked}
              </p>
            )}
            <Input
              type="password"
              value={newApiKey}
              onChange={(e) => setNewApiKey(e.target.value)}
              placeholder={settings?.api_key_set ? 'Enter new key to replace...' : 'zv_live_...'}
              data-testid="zavu-api-key-input"
              className="h-12 border-2 rounded-xl font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Get your API key from <a href="https://dashboard.zavu.dev" target="_blank" rel="noreferrer" className="text-primary underline">dashboard.zavu.dev</a>
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold uppercase tracking-wide">Country Code</Label>
            <Input
              value={settings?.country_code || '+91'}
              onChange={(e) => setSettings({ ...settings, country_code: e.target.value })}
              placeholder="+91"
              data-testid="country-code-input"
              className="h-12 border-2 rounded-xl w-32"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold uppercase tracking-wide">WhatsApp From Number</Label>
            <Input
              value={settings?.whatsapp_from_number || ''}
              onChange={(e) => setSettings({ ...settings, whatsapp_from_number: e.target.value })}
              placeholder="+16602326621"
              data-testid="whatsapp-from-input"
              className="h-12 border-2 rounded-xl font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Display reference only — Zavu auto-routes via your sender profile
            </p>
          </div>
        </Card>
      </motion.div>

      {/* iter199 — Provider Selector */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <Card className="p-5 border-2 rounded-2xl space-y-4" data-testid="provider-selector-card">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-lg">Provider</h3>
          </div>
          <p className="text-xs text-muted-foreground">
            Pick which transport actually sends every WhatsApp notification. You can flip this any time — switch is instant, no redeploy.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setSettings({ ...settings, provider: 'zavu' })}
              data-testid="provider-zavu-btn"
              className={`p-4 rounded-2xl border-2 text-left transition-all ${
                (settings?.provider || 'zavu') === 'zavu'
                  ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                  : 'border-border hover:border-primary/40'
              }`}
            >
              <p className="font-semibold text-sm">Zavu</p>
              <p className="text-xs text-muted-foreground mt-1 leading-snug">
                Aggregator with SMS fallback. Easier setup, slightly higher per-message cost.
              </p>
            </button>
            <button
              type="button"
              onClick={() => setSettings({ ...settings, provider: 'meta' })}
              data-testid="provider-meta-btn"
              className={`p-4 rounded-2xl border-2 text-left transition-all ${
                settings?.provider === 'meta'
                  ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                  : 'border-border hover:border-primary/40'
              }`}
            >
              <p className="font-semibold text-sm">Meta Cloud API</p>
              <p className="text-xs text-muted-foreground mt-1 leading-snug">
                Direct from Meta. Lower cost per conversation, requires approved templates outside 24h window.
              </p>
            </button>
          </div>
        </Card>
      </motion.div>

      {/* iter199 — Meta Cloud API config (visible only when provider=meta) */}
      {settings?.provider === 'meta' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="p-5 border-2 rounded-2xl space-y-4" data-testid="meta-config-card">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold text-lg">Meta WhatsApp Cloud API</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              From your Meta Developer dashboard. Tokens never leave the backend — they&apos;re masked in this UI.
            </p>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Phone Number ID</Label>
              <Input
                value={settings?.meta_phone_number_id || ''}
                onChange={(e) => setSettings({ ...settings, meta_phone_number_id: e.target.value })}
                placeholder="1234567890123456"
                data-testid="meta-phone-number-id-input"
                className="h-11 border-2 rounded-xl font-mono text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">App ID</Label>
              <Input
                value={settings?.meta_app_id || ''}
                onChange={(e) => setSettings({ ...settings, meta_app_id: e.target.value })}
                placeholder="1132093045106799"
                data-testid="meta-app-id-input"
                className="h-11 border-2 rounded-xl font-mono text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">WhatsApp Business Account ID</Label>
              <Input
                value={settings?.meta_business_account_id || ''}
                onChange={(e) => setSettings({ ...settings, meta_business_account_id: e.target.value })}
                placeholder="1671899157422842"
                data-testid="meta-waba-id-input"
                className="h-11 border-2 rounded-xl font-mono text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Permanent Access Token</Label>
              {settings?.meta_access_token_set && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Token saved: {settings.meta_access_token_masked}
                </p>
              )}
              <Input
                type="password"
                value={newMetaAccessToken}
                onChange={(e) => setNewMetaAccessToken(e.target.value)}
                placeholder={settings?.meta_access_token_set ? 'Enter new token to replace…' : 'EAAQ8sUf6...'}
                data-testid="meta-access-token-input"
                className="h-11 border-2 rounded-xl font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground leading-snug">
                Generate at <span className="font-mono">Business Settings → System Users → Generate new token</span>.
                Assign your WhatsApp app + permissions <code>whatsapp_business_messaging</code> and <code>whatsapp_business_management</code>.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">App Secret</Label>
              {settings?.meta_app_secret_set && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Secret saved: {settings.meta_app_secret_masked}
                </p>
              )}
              <Input
                type="password"
                value={newMetaAppSecret}
                onChange={(e) => setNewMetaAppSecret(e.target.value)}
                placeholder={settings?.meta_app_secret_set ? 'Enter new secret to replace…' : 'a9f405...'}
                data-testid="meta-app-secret-input"
                className="h-11 border-2 rounded-xl font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Used to verify webhook signatures. Meta App → Settings → Basic → App Secret.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Graph API version</Label>
              <Input
                value={settings?.meta_graph_version || 'v22.0'}
                onChange={(e) => setSettings({ ...settings, meta_graph_version: e.target.value })}
                placeholder="v22.0"
                data-testid="meta-graph-version-input"
                className="h-11 border-2 rounded-xl font-mono text-sm w-32"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Outside 24-h window behaviour</Label>
              <Select
                value={settings?.meta_outside_window_behavior || 'skip'}
                onValueChange={(val) => setSettings({ ...settings, meta_outside_window_behavior: val })}
              >
                <SelectTrigger data-testid="meta-outside-window-select" className="h-11 border-2 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip silently (recommended until templates approved)</SelectItem>
                  <SelectItem value="try">Try anyway (will hit error 131047)</SelectItem>
                  <SelectItem value="fallback_zavu">Fall back to Zavu for that message</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Webhook URL & verify token block */}
            <div className="rounded-2xl bg-muted/40 border-2 border-dashed border-border p-4 space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Paste these into Meta Dashboard
              </p>
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">Callback URL</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono bg-background border-2 rounded-lg px-2 py-1.5 break-all" data-testid="meta-webhook-url">
                    https://split-easy.in/api/whatsapp/webhook
                  </code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard('https://split-easy.in/api/whatsapp/webhook', 'Webhook URL')}
                    data-testid="meta-webhook-url-copy"
                    className="p-1.5 rounded-md hover:bg-muted shrink-0"
                    aria-label="Copy webhook URL"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">Verify token</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono bg-background border-2 rounded-lg px-2 py-1.5 break-all" data-testid="meta-verify-token">
                    {settings?.meta_verify_token || '—'}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(settings?.meta_verify_token, 'Verify token')}
                    data-testid="meta-verify-token-copy"
                    className="p-1.5 rounded-md hover:bg-muted shrink-0"
                    aria-label="Copy verify token"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground leading-snug">
                In Meta Dashboard → WhatsApp → Configuration → Webhook. After verification, subscribe to <code>messages</code> field for the WABA.
              </p>
            </div>

            {/* iter213 — Test via APPROVED TEMPLATE (works outside 24h window) */}
            <div className="space-y-2 pt-3 border-t-2 border-dashed border-emerald-400/40" data-testid="meta-test-template-block">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="text-sm font-semibold uppercase tracking-wide flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  Send test via approved template
                </Label>
                <span className="text-xs uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 dark:bg-emerald-500/15 dark:text-emerald-300 px-2 py-0.5 rounded-full">
                  Works outside 24h window
                </span>
              </div>
              <p className="text-xs text-muted-foreground leading-snug">
                Free-form text only works for users who messaged you in the last 24h. <strong>Approved templates</strong> work for anyone, any time — use this to confirm Meta delivery end-to-end on your phone.
              </p>

              {approvedTemplates.length === 0 ? (
                <div className="rounded-xl border-2 border-dashed border-amber-400/50 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-300" data-testid="meta-test-template-empty">
                  <p className="font-semibold">No APPROVED templates yet.</p>
                  <p className="leading-snug mt-0.5">Hit <strong>Sync</strong> in the Message Templates card above, wait for Meta to approve at least one (usually within minutes), then come back here.</p>
                </div>
              ) : (
                <>
                  <Select value={testTplName} onValueChange={setTestTplName}>
                    <SelectTrigger
                      data-testid="meta-test-template-select"
                      className="h-11 border-2 rounded-xl text-sm"
                    >
                      <SelectValue placeholder="Pick a template…" />
                    </SelectTrigger>
                    <SelectContent>
                      {approvedTemplates.map((t) => (
                        <SelectItem key={t.name} value={t.name} data-testid={`meta-test-template-option-${t.name}`}>
                          <span className="font-mono text-xs">{t.name}</span>
                          {t.param_count > 0 && (
                            <span className="ml-2 text-xs text-muted-foreground">({t.param_count} params)</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const sel = approvedTemplates.find((t) => t.name === testTplName);
                    if (!sel) return null;
                    return (
                      <code className="block text-xs font-mono bg-muted/40 rounded-md px-2 py-1.5 text-muted-foreground break-words" data-testid="meta-test-template-preview">
                        {sel.body}
                      </code>
                    );
                  })()}
                  <div className="flex gap-2">
                    <Input
                      type="tel"
                      value={testTplPhone}
                      onChange={(e) => setTestTplPhone(e.target.value)}
                      placeholder="9820030046"
                      data-testid="meta-test-template-phone-input"
                      className="h-11 border-2 rounded-xl font-mono text-sm flex-1"
                    />
                    <Button
                      onClick={handleTestTemplate}
                      disabled={testingTpl || testingBatch || !testTplName}
                      data-testid="meta-test-template-send-btn"
                      className="rounded-xl bg-emerald-600 hover:bg-emerald-700"
                    >
                      {testingTpl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      <span className="ml-1">Send template</span>
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground italic leading-snug">
                    Test uses placeholder values (e.g. <em>Kalpesh, Tuesday Pickle, Rs 540…</em>) — your real notifications fill them dynamically.
                  </p>

                  {/* iter213.1 — Test ALL templates button + results */}
                  <div className="pt-3 mt-2 border-t border-dashed border-emerald-400/30 space-y-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Label className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5">
                        <Zap className="w-3.5 h-3.5 text-amber-500" />
                        Smoke-test ALL templates
                      </Label>
                      <span className="text-xs text-muted-foreground">
                        {approvedTemplates.length} APPROVED
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-snug">
                      Fires <strong>one of each</strong> approved template to the phone above (400ms apart). Visually confirm every notification path in one go — fines, RSVP, waitlist, balance reminders, all of them.
                    </p>
                    <Button
                      onClick={handleTestTemplateBatch}
                      disabled={testingBatch || testingTpl || !testTplPhone.trim() || approvedTemplates.length === 0}
                      data-testid="meta-test-template-batch-btn"
                      className="w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white"
                    >
                      {testingBatch ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Sending {approvedTemplates.length}…</>
                      ) : (
                        <><Send className="w-4 h-4 mr-1.5" /> Send all {approvedTemplates.length} templates</>
                      )}
                    </Button>

                    {batchResults && (
                      <div className="rounded-xl border-2 p-3 space-y-2 bg-muted/20" data-testid="meta-test-template-batch-results">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="text-xs font-semibold">
                            Sent to <code className="font-mono">{batchResults.to}</code>
                          </p>
                          <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold">
                            <span className="text-emerald-700 bg-emerald-100 dark:bg-emerald-500/15 dark:text-emerald-300 px-1.5 py-0.5 rounded-full" data-testid="batch-results-sent">
                              {batchResults.sent} sent
                            </span>
                            {batchResults.failed > 0 && (
                              <span className="text-red-700 bg-red-100 dark:bg-red-500/15 dark:text-red-300 px-1.5 py-0.5 rounded-full" data-testid="batch-results-failed">
                                {batchResults.failed} failed
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto space-y-1" data-testid="batch-results-list">
                          {(batchResults.results || []).map((r) => (
                            <div
                              key={r.name}
                              className="flex items-start gap-2 text-xs py-1.5 border-b last:border-b-0 border-border/40"
                              data-testid={`batch-result-${r.name}`}
                            >
                              {r.ok ? (
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                              ) : (
                                <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                              )}
                              <div className="flex-1 min-w-0">
                                <code className="font-mono text-xs block truncate">{r.name}</code>
                                {!r.ok && (
                                  <p className="text-red-600 text-xs">
                                    {r.error_code ? `[${r.error_code}] ` : ''}{r.error_title}
                                  </p>
                                )}
                                {r.ok && r.message_id && (
                                  <p className="text-muted-foreground text-xs font-mono truncate">{r.message_id}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* iter214.1 — Removed free-form "Send test" block. It only worked
                inside Meta's 24h customer-service window and always failed
                otherwise (error 131047), confusing admins. The "Send template"
                button above is the correct single test path for Meta. */}
          </Card>
        </motion.div>
      )}

      {/* iter199.1 — Templates panel (Meta only) */}
      {settings?.provider === 'meta' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}>
          <Card className="p-5 border-2 rounded-2xl space-y-4" data-testid="templates-card">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Zap className="w-5 h-5 text-amber-500 shrink-0" />
                <h3 className="font-semibold text-lg truncate">Message Templates</h3>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRefreshTemplates}
                  disabled={refreshingTpl || !settings?.meta_access_token_set}
                  data-testid="templates-refresh-btn"
                  className="rounded-full text-xs h-8"
                >
                  {refreshingTpl ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  <span className="ml-1 hidden sm:inline">Refresh</span>
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleSyncTemplates(null)}
                  disabled={syncingTpl || !settings?.meta_access_token_set}
                  data-testid="templates-sync-btn"
                  className="rounded-full text-xs h-8"
                >
                  {syncingTpl ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  <span className="ml-1">Sync all</span>
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Submits SplitEasy templates to Meta for approval. <strong>Already-synced templates are SKIPPED automatically</strong> — only missing ones get submitted. Auto-bumps the name (<code>_v2</code>, <code>_v3</code>…) when Meta locks a rejection. A daily auto-sync (9 AM IST) submits any newly added system templates for you.
            </p>

            {/* iter262 — template health banner */}
            {tplHealth && (tplHealth.missing || []).length > 0 && (
              <div className="rounded-xl border-2 border-amber-400/50 bg-amber-50 dark:bg-amber-500/10 p-3" data-testid="template-health-banner">
                <p className="text-xs font-bold text-amber-800 dark:text-amber-300">
                  ⚠️ {tplHealth.missing.length} notification type{tplHealth.missing.length === 1 ? '' : 's'} have NO approved template — sends to new numbers will fail:
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-200 mt-1 font-mono break-words">
                  {tplHealth.missing.join(', ')}
                </p>
                <p className="text-xs text-amber-700/80 dark:text-amber-200/80 mt-1">
                  Hit <strong>Sync all</strong>, wait a few minutes for Meta approval, then <strong>Refresh</strong>.
                </p>
              </div>
            )}
            {tplHealth && (tplHealth.missing || []).length === 0 && (
              <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold" data-testid="template-health-ok">
                ✓ All {tplHealth.total} notification types have an approved template
              </p>
            )}

            {/* iter267 — template format lint (Meta rules) */}
            {tplHealth && (tplHealth.lint_issues || []).length > 0 && (
              <div className="rounded-xl border-2 border-rose-400/50 bg-rose-50 dark:bg-rose-500/10 p-3" data-testid="template-lint-banner">
                <p className="text-xs font-bold text-rose-800 dark:text-rose-300">
                  🧹 {tplHealth.lint_issues.length} template{tplHealth.lint_issues.length === 1 ? '' : 's'} break Meta formatting rules:
                </p>
                <ul className="mt-1 space-y-0.5">
                  {tplHealth.lint_issues.map((li) => (
                    <li key={li.notif_type} className="text-xs text-rose-700 dark:text-rose-200">
                      <span className="font-mono font-semibold">{li.notif_type}</span>
                      {' — '}{[...(li.errors || []), ...(li.warnings || [])].join('; ')}
                      {li.auto_fixable
                        ? <span className="text-emerald-700 dark:text-emerald-300 font-semibold"> (auto-corrected at submission ✓)</span>
                        : <span className="font-semibold"> (fix in catalog before syncing!)</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* iter213.3 — Selective sync toolbar */}
            {(templates.catalog || []).length > 0 && (
              <div className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 p-2.5 space-y-2" data-testid="templates-selective-toolbar">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs font-semibold uppercase tracking-wide">
                    Selected: <span data-testid="templates-selected-count">{selectedNotifTypes.size}</span> / {(templates.catalog || []).length}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const allTypes = (templates.catalog || []).map((c) => c.notif_type);
                        setSelectedNotifTypes(new Set(allTypes));
                      }}
                      disabled={syncingTpl}
                      data-testid="templates-select-all"
                      className="text-xs h-7 rounded-full"
                    >
                      All
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        // Select only NOT_SUBMITTED catalog rows
                        const all = templates.templates || [];
                        const missing = (templates.catalog || [])
                          .filter((cat) => {
                            const base = cat.base_name;
                            const prefix = base.endsWith('_v1') ? base.slice(0, -2) : base + '_v';
                            const candidateNames = [base, ...[2, 3, 4, 5, 6].map((i) => `${prefix}${i}`)];
                            const remote =
                              all.find((t) => t.splitease_notif_type === cat.notif_type && t.splitease_active) ||
                              all.find((t) => candidateNames.includes(t.name));
                            return !remote;
                          })
                          .map((c) => c.notif_type);
                        setSelectedNotifTypes(new Set(missing));
                      }}
                      disabled={syncingTpl}
                      data-testid="templates-select-missing"
                      className="text-xs h-7 rounded-full"
                    >
                      Missing only
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedNotifTypes(new Set())}
                      disabled={syncingTpl || selectedNotifTypes.size === 0}
                      data-testid="templates-select-none"
                      className="text-xs h-7 rounded-full"
                    >
                      None
                    </Button>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleSyncTemplates(Array.from(selectedNotifTypes))}
                  disabled={syncingTpl || !settings?.meta_access_token_set || selectedNotifTypes.size === 0}
                  data-testid="templates-sync-selected-btn"
                  className="w-full rounded-full text-xs h-9 bg-primary hover:bg-primary/90"
                >
                  {syncingTpl ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Syncing {selectedNotifTypes.size}…</>
                  ) : (
                    <><Send className="w-3.5 h-3.5 mr-1.5" /> Sync selected ({selectedNotifTypes.size})</>
                  )}
                </Button>
              </div>
            )}

            {/* iter214.1 — Last sync result panel with per-template rejection reasons */}
            {syncResult && (
              <div
                className="rounded-xl border-2 border-dashed border-amber-500/40 bg-amber-500/5 p-3 space-y-2"
                data-testid="templates-sync-result"
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs font-semibold uppercase tracking-wide">
                    Last sync result
                  </p>
                  <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold">
                    <span
                      className="text-emerald-700 bg-emerald-100 dark:bg-emerald-500/15 dark:text-emerald-300 px-1.5 py-0.5 rounded-full"
                      data-testid="sync-result-submitted"
                    >
                      {(syncResult.submitted || []).length} submitted
                    </span>
                    <span
                      className="text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded-full"
                      data-testid="sync-result-skipped"
                    >
                      {(syncResult.skipped || []).length} skipped
                    </span>
                    {(syncResult.failed || []).length > 0 && (
                      <span
                        className="text-red-700 bg-red-100 dark:bg-red-500/15 dark:text-red-300 px-1.5 py-0.5 rounded-full"
                        data-testid="sync-result-failed"
                      >
                        {(syncResult.failed || []).length} failed
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setSyncResult(null)}
                      data-testid="sync-result-dismiss"
                      className="text-xs text-muted-foreground hover:text-foreground underline ml-1"
                    >
                      dismiss
                    </button>
                  </div>
                </div>
                {(syncResult.failed || []).length > 0 && (
                  <div className="space-y-1.5" data-testid="sync-result-failed-list">
                    {(syncResult.failed || []).map((f, i) => (
                      <div
                        key={`${f.notif_type || f.name || i}`}
                        className="rounded-lg border border-red-500/30 bg-red-500/5 p-2 text-xs space-y-0.5"
                        data-testid={`sync-result-failed-row-${f.notif_type || i}`}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                          <code className="font-mono text-xs font-semibold">
                            {f.notif_type || f.name || 'template'}
                          </code>
                          {f.code && (
                            <span className="text-xs uppercase tracking-wider font-bold text-red-700 dark:text-red-300 bg-red-500/10 px-1.5 py-0.5 rounded-full">
                              code {f.code}
                            </span>
                          )}
                          {f.base_name && f.base_name !== f.name && (
                            <code className="font-mono text-xs text-muted-foreground">
                              tried: {f.name || f.base_name}
                            </code>
                          )}
                        </div>
                        <p className="text-red-700 dark:text-red-300 leading-snug pl-5 break-words">
                          {f.error || 'unknown error'}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                {(syncResult.submitted || []).length > 0 && (
                  <details className="text-xs text-muted-foreground">
                    <summary className="cursor-pointer">Submitted ({(syncResult.submitted || []).length})</summary>
                    <div className="pt-1 space-y-0.5">
                      {(syncResult.submitted || []).map((s, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                          <code className="font-mono text-xs">{s.notif_type}</code>
                          <span className="text-xs">→ {s.name}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}

            {/* Catalog → status table */}
            <div className="space-y-2">
              {/* iter213.2 — Meta-side summary banner so admin can audit live state */}
              {(templates.templates || []).length > 0 && (() => {
                const all = templates.templates || [];
                const approved = all.filter((t) => t.status === 'APPROVED').length;
                const pending = all.filter((t) => t.status === 'PENDING').length;
                const rejected = all.filter((t) => t.status === 'REJECTED').length;
                const latest = all
                  .map((t) => t.fetched_at)
                  .filter(Boolean)
                  .sort()
                  .pop();
                return (
                  <div className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 p-2.5 text-xs flex items-center justify-between gap-2 flex-wrap" data-testid="templates-meta-summary">
                    <span>
                      Meta has <strong>{all.length}</strong> templates total
                      {' · '}
                      <span className="text-emerald-700 dark:text-emerald-300">{approved} APPROVED</span>
                      {' · '}
                      <span className="text-amber-700 dark:text-amber-300">{pending} PENDING</span>
                      {rejected > 0 && (
                        <>
                          {' · '}
                          <span className="text-red-700 dark:text-red-300">{rejected} REJECTED</span>
                        </>
                      )}
                    </span>
                    {latest && (
                      <span className="text-muted-foreground" data-testid="templates-meta-fetched">
                        Last fetched: {new Date(latest).toLocaleString()}
                      </span>
                    )}
                  </div>
                );
              })()}

              {(templates.catalog || []).map((cat) => {
                // iter213.2 — Candidate-name-aware matching. Catalog base_name
                // always ends with `_v1`; Meta may have v1..v6 if earlier
                // versions were rejected. We try the backend mapping first
                // (splitease_notif_type), then fall back to ANY name in the
                // candidate sequence, then the base_name. This makes the UI
                // robust even when the backend mapping wasn't persisted.
                const base = cat.base_name; // e.g. splitease_rsvp_v1
                const prefix = base.endsWith('_v1') ? base.slice(0, -2) : base + '_v'; // splitease_rsvp_v
                const candidateNames = [base, ...[2, 3, 4, 5, 6].map((i) => `${prefix}${i}`)];
                const allTpls = templates.templates || [];
                const remote =
                  allTpls.find((t) => t.splitease_notif_type === cat.notif_type && t.splitease_active) ||
                  allTpls.find((t) => candidateNames.includes(t.name)) ||
                  allTpls.find((t) => t.name === cat.base_name);
                const status = remote?.status || 'NOT_SUBMITTED';
                const statusClasses = {
                  APPROVED: 'border-emerald-500/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10',
                  PENDING: 'border-amber-500/40 text-amber-700 dark:text-amber-300 bg-amber-500/10',
                  REJECTED: 'border-red-500/40 text-red-700 dark:text-red-300 bg-red-500/10',
                  NOT_SUBMITTED: 'border-muted-foreground/40 text-muted-foreground bg-muted/30',
                }[status] || 'border-muted-foreground/40 text-muted-foreground bg-muted/30';
                return (
                  <div
                    key={cat.notif_type}
                    className="rounded-xl border-2 p-3 space-y-1.5"
                    data-testid={`template-row-${cat.notif_type}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0">
                        <input
                          type="checkbox"
                          checked={selectedNotifTypes.has(cat.notif_type)}
                          onChange={(e) => {
                            const next = new Set(selectedNotifTypes);
                            if (e.target.checked) next.add(cat.notif_type);
                            else next.delete(cat.notif_type);
                            setSelectedNotifTypes(next);
                          }}
                          disabled={syncingTpl}
                          data-testid={`template-checkbox-${cat.notif_type}`}
                          className="w-4 h-4 rounded border-2 border-primary/40 cursor-pointer shrink-0 accent-primary"
                          aria-label={`Select ${cat.notif_type} for sync`}
                        />
                        <p className="font-semibold text-sm capitalize truncate">
                          {cat.notif_type.replace(/_/g, ' ')}
                        </p>
                        <span
                          className={`text-xs uppercase tracking-wider font-bold border px-1.5 py-0.5 rounded-full ${statusClasses}`}
                          data-testid={`template-status-${cat.notif_type}`}
                        >
                          {status}
                        </span>
                      </div>
                      {remote?.name && status !== 'NOT_SUBMITTED' && (
                        <button
                          type="button"
                          onClick={() => handleDeleteTemplate(remote.name)}
                          disabled={deletingTpl === remote.name}
                          data-testid={`template-delete-${cat.notif_type}`}
                          className="text-xs text-red-600 hover:text-red-700 font-medium shrink-0"
                        >
                          {deletingTpl === remote.name ? '…' : 'Delete'}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground italic leading-snug">{cat.description}</p>
                    <code className="block text-xs font-mono bg-muted/40 rounded-md px-2 py-1.5 text-muted-foreground break-all">
                      {cat.body}
                    </code>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      <span>Name: <code className="font-mono">{remote?.name || cat.base_name}</code></span>
                      <span>Category: <code className="font-mono">{remote?.category || cat.category}</code></span>
                      {remote?.fetched_at && (
                        <span data-testid={`template-fetched-${cat.notif_type}`}>
                          Fetched: <code className="font-mono">{new Date(remote.fetched_at).toLocaleTimeString()}</code>
                        </span>
                      )}
                    </div>
                    {remote?.rejected_reason && (
                      <p className="text-xs text-red-600">
                        Rejected: {remote.rejected_reason}
                      </p>
                    )}
                  </div>
                );
              })}
              {(templates.catalog || []).length === 0 && (
                <p className="text-xs text-muted-foreground italic">No catalogue loaded.</p>
              )}
            </div>

            {!settings?.meta_access_token_set && (
              <p className="text-xs text-amber-600 border-t border-border/60 pt-3">
                Paste the Permanent Access Token + Save before syncing.
              </p>
            )}
          </Card>
        </motion.div>
      )}

      {/* iter208 — Webhook Fan-out (Meta only, super-admin only) */}
      {settings?.provider === 'meta' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.13 }}>
          <Card className="p-5 border-2 rounded-2xl space-y-4" data-testid="fanout-card">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Webhook className="w-5 h-5 text-indigo-500 shrink-0" />
                <h3 className="font-semibold text-lg truncate">Webhook Fan-out</h3>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchFanoutLog}
                disabled={refreshingFanoutLog}
                data-testid="fanout-log-refresh-btn"
                className="rounded-full text-xs h-8 shrink-0"
              >
                {refreshingFanoutLog ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                <span className="ml-1 hidden sm:inline">Refresh log</span>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Meta allows only <strong>one</strong> webhook URL. SplitEasy receives the callback, verifies the signature, then forwards the <em>exact raw payload + signature header</em> to every enabled downstream below (e.g. your Odoo ERP). Failures here never block the response to Meta.
            </p>

            {/* SplitEasy Webhook URL & Verify Token block */}
            <div className="rounded-xl border-2 border-dashed border-indigo-400/50 bg-indigo-500/5 p-3 space-y-2">
              <p className="text-xs uppercase tracking-wider font-bold text-indigo-700 dark:text-indigo-300">
                Configure in Meta dashboard
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Callback URL</Label>
                <div className="flex items-center gap-1.5">
                  <code className="flex-1 text-xs font-mono bg-background border rounded-md px-2 py-1.5 truncate" data-testid="fanout-splitease-url">
                    {fanout.splitease_webhook_url || '—'}
                  </code>
                  <Button size="sm" variant="ghost" onClick={handleCopyWebhookUrl} data-testid="fanout-copy-url" className="h-8 px-2">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Verify Token</Label>
                <div className="flex items-center gap-1.5">
                  <code className="flex-1 text-xs font-mono bg-background border rounded-md px-2 py-1.5 truncate" data-testid="fanout-verify-token">
                    {fanout.splitease_verify_token || '—'}
                  </code>
                  <Button size="sm" variant="ghost" onClick={handleCopyVerifyToken} data-testid="fanout-copy-token" className="h-8 px-2">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              {!fanout.app_secret_configured && (
                <p className="text-xs text-amber-600 leading-snug">
                  ⚠️ App Secret not set — signature verification will be skipped. Paste your App Secret in the Meta config card above.
                </p>
              )}
            </div>

            {/* Downstreams list */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide">Downstream Endpoints ({(fanout.downstreams || []).length})</Label>
              {(fanout.downstreams || []).length === 0 ? (
                <p className="text-xs text-muted-foreground italic p-3 border rounded-xl bg-muted/20" data-testid="fanout-empty">
                  No downstreams configured yet. Add your Odoo webhook URL below.
                </p>
              ) : (
                (fanout.downstreams || []).map((d, idx) => (
                  <div
                    key={`${d.url}-${idx}`}
                    className="rounded-xl border-2 p-3 space-y-1.5"
                    data-testid={`fanout-row-${idx}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm truncate flex items-center gap-1.5">
                        {d.enabled ? (
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                        ) : (
                          <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                        )}
                        {d.label}
                      </p>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Switch
                          checked={!!d.enabled}
                          onCheckedChange={(val) => handleToggleDownstream(idx, val)}
                          disabled={savingFanout}
                          data-testid={`fanout-toggle-${idx}`}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteDownstream(idx)}
                          disabled={savingFanout}
                          data-testid={`fanout-delete-${idx}`}
                          className="h-8 px-2 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    <code className="block text-xs font-mono bg-muted/40 rounded-md px-2 py-1.5 text-muted-foreground break-all">
                      {d.url}
                    </code>
                  </div>
                ))
              )}
            </div>

            {/* Add new downstream */}
            <div className="rounded-xl border-2 p-3 space-y-2 bg-muted/10">
              <Label className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add Downstream
              </Label>
              <Input
                value={newFanoutLabel}
                onChange={(e) => setNewFanoutLabel(e.target.value)}
                placeholder="Label (e.g. Odoo ERP)"
                data-testid="fanout-new-label"
                className="h-10 border-2 rounded-xl text-sm"
              />
              <Input
                value={newFanoutUrl}
                onChange={(e) => setNewFanoutUrl(e.target.value)}
                placeholder="https://erp.example.com/whatsapp/webhook"
                data-testid="fanout-new-url"
                className="h-10 border-2 rounded-xl text-sm font-mono"
              />
              <Button
                size="sm"
                onClick={handleAddDownstream}
                disabled={savingFanout || !newFanoutUrl.trim()}
                data-testid="fanout-add-btn"
                className="w-full rounded-full"
              >
                {savingFanout ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                Add downstream
              </Button>
            </div>

            {/* Recent deliveries log */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide">Recent Deliveries</Label>
              {fanoutLog.length === 0 ? (
                <p className="text-xs text-muted-foreground italic p-3 border rounded-xl bg-muted/20" data-testid="fanout-log-empty">
                  No deliveries yet. Once Meta calls your webhook, every fan-out attempt will appear here.
                </p>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="fanout-log-list">
                  {fanoutLog.map((row) => (
                    <div
                      key={row.id}
                      className="rounded-xl border p-2 text-xs flex items-start gap-2"
                      data-testid={`fanout-log-row-${row.id}`}
                    >
                      {row.ok ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0 space-y-0.5">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="font-semibold truncate">{row.label}</span>
                          <span className="text-muted-foreground text-xs">
                            {row.status_code} · {row.duration_ms}ms
                          </span>
                        </div>
                        <code className="block text-xs font-mono text-muted-foreground break-all">
                          {row.url}
                        </code>
                        {row.error && (
                          <p className="text-red-600 text-xs">Error: {row.error}</p>
                        )}
                        <p className="text-muted-foreground text-xs">{new Date(row.started_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      )}

      {/* iter212 — Outbound WhatsApp Message Log (Meta only, super-admin) */}
      {settings?.provider === 'meta' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}>
          <WhatsAppMessageLogCard
            apiPath="/whatsapp/messages"
            title="Outbound Messages"
            refreshInterval={30000}
            allowRetry={true}
            defaultLimit={50}
            testIdPrefix="wa-msglog-global"
          />
        </motion.div>
      )}

      {/* iter214.7 — Threaded WhatsApp Chat panel */}
      {settings?.provider === 'meta' && (
        <motion.div id="chat" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <WhatsAppConversationPanel />
        </motion.div>
      )}

      {/* iter214 — Inbound WhatsApp Messages (Phase 1, super-admin) */}
      {settings?.provider === 'meta' && (
        <motion.div id="inbound" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}>
          <WhatsAppInboundLogCard
            apiPath="/whatsapp/inbound"
            title="Inbound Messages"
            refreshInterval={30000}
            defaultLimit={50}
          />
        </motion.div>
      )}

      {/* iter214 Phase 2 — WhatsApp Disputes (button-triggered, super-admin) */}
      {settings?.provider === 'meta' && (
        <motion.div id="disputes" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}>
          <WhatsAppDisputesCard
            apiPath="/whatsapp/disputes"
            title="Open Disputes"
            refreshInterval={30000}
            defaultLimit={50}
          />
        </motion.div>
      )}

      {/* Master Toggle */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <Card className="p-5 border-2 rounded-2xl" data-testid="master-toggle-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {settings?.enabled ? (
                <Bell className="w-5 h-5 text-green-500" />
              ) : (
                <BellOff className="w-5 h-5 text-muted-foreground" />
              )}
              <div>
                <Label className="font-semibold text-base">Enable WhatsApp Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  {settings?.enabled ? 'Notifications are active' : 'All WhatsApp notifications are off'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings?.enabled || false}
              onCheckedChange={(val) => setSettings({ ...settings, enabled: val })}
              data-testid="whatsapp-master-toggle"
            />
          </div>
        </Card>
      </motion.div>

      {/* SMS Fallback */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.07 }}>
        <Card className="p-5 border-2 rounded-2xl space-y-4" data-testid="sms-fallback-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Zap className="w-5 h-5 text-orange-500" />
              <div>
                <Label className="font-semibold text-base">SMS Fallback</Label>
                <p className="text-sm text-muted-foreground">
                  Auto-deliver via SMS if WhatsApp delivery fails (uses your Zavu credits)
                </p>
              </div>
            </div>
            <Switch
              checked={settings?.sms_fallback_enabled !== false}
              onCheckedChange={(val) => setSettings({ ...settings, sms_fallback_enabled: val })}
              data-testid="sms-fallback-toggle"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide font-semibold">SMS Sender Profile ID</Label>
            <Input
              value={settings?.sms_sender_profile_id || ''}
              onChange={(e) => setSettings({ ...settings, sms_sender_profile_id: e.target.value })}
              placeholder="e.g. kd7f05bpkn3vsaf8zns2122ccd85w2db"
              data-testid="sms-sender-profile-input"
              className="h-11 border-2 rounded-xl font-mono text-xs"
              disabled={!settings?.sms_fallback_enabled}
            />
            <p className="text-xs text-muted-foreground">
              Find it at Zavu Dashboard → Sender Profiles → SMS One-Way → API ID
            </p>
          </div>
          <div className="space-y-2 pt-2 border-t border-border">
            <Label className="text-xs uppercase tracking-wide font-semibold">Send Test SMS (direct)</Label>
            <div className="flex gap-2">
              <Input
                type="tel"
                value={testSmsPhone}
                onChange={(e) => setTestSmsPhone(e.target.value)}
                placeholder="9820030046"
                data-testid="test-sms-input"
                className="h-11 border-2 rounded-xl flex-1"
                disabled={!settings?.sms_fallback_enabled}
              />
              <Button
                onClick={handleTestSms}
                disabled={testingSms || !settings?.sms_fallback_enabled}
                data-testid="test-sms-btn"
                className="h-11 rounded-xl"
              >
                {testingSms ? <Loader2 className="w-4 h-4 animate-spin" /> : <><TestTube className="w-4 h-4 mr-1" /> Send</>}
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Email Channel */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.085 }}>
        <Card className="p-5 border-2 rounded-2xl space-y-4" data-testid="email-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-blue-500" />
              <div>
                <Label className="font-semibold text-base">Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Send notifications via email (separate channel — requires Zavu KYC)
                </p>
              </div>
            </div>
            <Switch
              checked={settings?.email_enabled || false}
              onCheckedChange={(val) => setSettings({ ...settings, email_enabled: val })}
              data-testid="email-toggle"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide font-semibold">Email Sender Profile ID</Label>
            <Input
              value={settings?.email_sender_profile_id || ''}
              onChange={(e) => setSettings({ ...settings, email_sender_profile_id: e.target.value })}
              placeholder="e.g. kd728fbepg648k64zsf8dpkwm585wms0"
              data-testid="email-sender-profile-input"
              className="h-11 border-2 rounded-xl font-mono text-xs"
              disabled={!settings?.email_enabled}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide font-semibold">From Address</Label>
            <Input
              type="email"
              value={settings?.email_from_address || ''}
              onChange={(e) => setSettings({ ...settings, email_from_address: e.target.value })}
              placeholder="e.g. devd@sandbox-mail-qu3zs1k5.zavu.lat"
              data-testid="email-from-input"
              className="h-11 border-2 rounded-xl text-sm"
              disabled={!settings?.email_enabled}
            />
          </div>
          <div className="space-y-2 pt-2 border-t border-border">
            <Label className="text-xs uppercase tracking-wide font-semibold">Send Test Email</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="recipient@example.com"
                data-testid="test-email-input"
                className="h-11 border-2 rounded-xl flex-1"
                disabled={!settings?.email_enabled}
              />
              <Button
                onClick={handleTestEmail}
                disabled={testingEmail || !settings?.email_enabled}
                data-testid="test-email-btn"
                className="h-11 rounded-xl"
              >
                {testingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <><TestTube className="w-4 h-4 mr-1" /> Send</>}
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Notification Toggles */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card className="border-2 rounded-2xl overflow-hidden" data-testid="notification-toggles-card">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold">Auto-Notification Types</h3>
            <p className="text-xs text-muted-foreground mt-1">Choose which events trigger a WhatsApp message</p>
          </div>

          {[
            { key: 'notify_on_fine', label: 'Fine Issued', desc: 'Notify member when they receive a fine' },
            { key: 'notify_on_expense_approval', label: 'Expense Approved/Rejected', desc: 'Notify creator when their expense is reviewed' },
            { key: 'notify_on_expense_submission', label: 'Expense Submitted', desc: 'Notify admins when a new expense needs approval' },
            { key: 'notify_on_session', label: 'Session Created', desc: 'Notify members when an attendance session is created' },
          ].map(({ key, label, desc }) => (
            <div key={key} className="p-4 flex items-center justify-between border-b border-border last:border-b-0">
              <div>
                <Label className="font-medium">{label}</Label>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
              <Switch
                checked={settings?.[key] || false}
                onCheckedChange={(val) => setSettings({ ...settings, [key]: val })}
                disabled={!settings?.enabled}
                data-testid={`toggle-${key}`}
              />
            </div>
          ))}
        </Card>
      </motion.div>

      {/* DND Hours (#8) */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.115 }}>
        <Card className="p-5 border-2 rounded-2xl space-y-4" data-testid="dnd-card">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            <div>
              <h3 className="font-semibold">Do Not Disturb</h3>
              <p className="text-xs text-muted-foreground">No WhatsApp messages during these hours (IST)</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide">Start (IST)</Label>
              <Select
                value={String(settings?.dnd_start_hour ?? 22)}
                onValueChange={(val) => setSettings({ ...settings, dnd_start_hour: parseInt(val) })}
              >
                <SelectTrigger className="h-10 rounded-xl" data-testid="dnd-start-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide">End (IST)</Label>
              <Select
                value={String(settings?.dnd_end_hour ?? 8)}
                onValueChange={(val) => setSettings({ ...settings, dnd_end_hour: parseInt(val) })}
              >
                <SelectTrigger className="h-10 rounded-xl" data-testid="dnd-end-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Default: 10 PM to 8 AM. In-app notifications still delivered.
          </p>
        </Card>
      </motion.div>

      {/* Weekly Digest Section */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}>
        <Card className="border-2 rounded-2xl overflow-hidden" data-testid="weekly-digest-card">
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                <div>
                  <h3 className="font-semibold">Weekly Digest</h3>
                  <p className="text-xs text-muted-foreground">Auto-send balance summary to all members</p>
                </div>
              </div>
              <Switch
                checked={settings?.weekly_digest || false}
                onCheckedChange={(val) => setSettings({ ...settings, weekly_digest: val })}
                disabled={!settings?.enabled}
                data-testid="toggle-weekly-digest"
              />
            </div>
          </div>

          {settings?.weekly_digest && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Day
                  </Label>
                  <Select
                    value={String(settings?.digest_day ?? 0)}
                    onValueChange={(val) => setSettings({ ...settings, digest_day: parseInt(val) })}
                  >
                    <SelectTrigger className="h-10 rounded-xl" data-testid="digest-day-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, i) => (
                        <SelectItem key={i} value={String(i)}>{day}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Time (IST)
                  </Label>
                  <Select
                    value={String(settings?.digest_hour_ist ?? 8)}
                    onValueChange={(val) => setSettings({ ...settings, digest_hour_ist: parseInt(val) })}
                  >
                    <SelectTrigger className="h-10 rounded-xl" data-testid="digest-hour-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Each member receives: their balance, pending expenses, and upcoming sessions.
              </p>

              <Button
                variant="outline"
                onClick={handleSendDigest}
                disabled={sendingDigest || !settings?.api_key_set}
                data-testid="send-digest-now-btn"
                className="w-full h-10 rounded-xl text-green-600 border-green-500/30 hover:bg-green-500/10"
              >
                {sendingDigest ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending Digest...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Send Digest Now</>
                )}
              </Button>
            </div>
          )}
        </Card>
      </motion.div>

      {/* iter254 — Reminders & Pings toggles */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.13 }}>
        <Card className="p-4 border-2 rounded-2xl space-y-4" data-testid="reminders-pings-card">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Reminders & Pings</h3>
          </div>
          {[
            {
              key: 'approval_reminders',
              title: 'Approval Reminders',
              desc: 'Nudge the right approver when an expense waits 3+ days (re-reminds every 3 days)',
              testId: 'toggle-approval-reminders',
            },
            {
              key: 'signup_ping',
              title: 'Signup Ping',
              desc: 'WhatsApp super admins the moment someone new signs up',
              testId: 'toggle-signup-ping',
            },
            {
              key: 'invite_reminders',
              title: 'Invite Reminders',
              desc: 'Re-invite pending numbers every 3 days until they join (max 3 pings)',
              testId: 'toggle-invite-reminders',
            },
          ].map((row) => (
            <div key={row.key} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold">{row.title}</p>
                <p className="text-xs text-muted-foreground">{row.desc}</p>
              </div>
              <Switch
                checked={settings?.[row.key] !== false}
                onCheckedChange={(val) => setSettings({ ...settings, [row.key]: val })}
                disabled={!settings?.enabled}
                data-testid={row.testId}
              />
            </div>
          ))}
        </Card>
      </motion.div>

      {/* Save Button */}
      <Button
        onClick={handleSave}
        disabled={saving}
        data-testid="save-whatsapp-settings-btn"
        className="w-full h-12 rounded-full font-bold uppercase tracking-wider"
      >
        {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Check className="w-5 h-5 mr-2" />}
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>

      {/* Test Section */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <Card className="p-5 border-2 rounded-2xl space-y-4" data-testid="test-message-card">
          <div className="flex items-center gap-2">
            <TestTube className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-lg">Test Message</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Send a test WhatsApp message to verify your integration is working.
          </p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="tel"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="9820030046"
                data-testid="test-phone-input"
                className="h-12 border-2 rounded-xl pl-10"
              />
            </div>
            <Button
              onClick={handleTest}
              disabled={testing || !settings?.api_key_set}
              data-testid="send-test-btn"
              className="h-12 rounded-xl px-6"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          {!settings?.api_key_set && (
            <p className="text-xs text-destructive">Save an API key first before testing</p>
          )}
        </Card>
      </motion.div>
    </div>
  );
}
